import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { API } from "@/lib/api";

const inputCls =
  "h-14 w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-lg font-semibold text-slate-800 outline-none transition-colors focus:border-sky-400 placeholder:font-medium placeholder:text-slate-400";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-2xl px-6 py-16">
      <Seo
        title="Contact Us | Divine Kids Learning"
        description="Questions about our printable preschool worksheets or your order? Send us a message."
      />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-green">Contact</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-800 sm:text-5xl">
          We'd love to hear from you
        </h1>
        <p className="mt-4 text-lg font-semibold text-slate-500">
          Questions about a product, your order or a download? Send us a message and we'll get
          back to you soon.
        </p>
      </motion.div>

      {sent ? (
        <div data-testid="contact-success" className="mt-10 rounded-[2rem] bg-white p-10 text-center shadow-xl shadow-blue-900/5">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <MailCheck className="h-8 w-8 text-brand-green" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-bold text-slate-800">Message sent!</h2>
          <p className="mt-2 font-semibold text-slate-500">
            Thank you for reaching out. We'll reply to your email soon.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-4 rounded-[2rem] bg-white p-8 shadow-xl shadow-blue-900/5">
          <input
            data-testid="contact-name-input"
            required
            minLength={2}
            placeholder="Your name"
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            data-testid="contact-email-input"
            required
            type="email"
            placeholder="Your email"
            className={inputCls}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <textarea
            data-testid="contact-message-input"
            required
            minLength={10}
            rows={5}
            placeholder="Your message"
            className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-4 text-lg font-semibold text-slate-800 outline-none transition-colors focus:border-sky-400 placeholder:font-medium placeholder:text-slate-400"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <button
            data-testid="contact-send-button"
            type="submit"
            disabled={loading}
            className="h-14 w-full rounded-full bg-brand-blue font-display text-xl font-bold text-white shadow-[0_5px_0_0_#1D4ED8] transition-transform active:translate-y-1 active:shadow-none disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="mx-auto h-6 w-6 animate-spin" />
            ) : (
              <span className="inline-flex items-center gap-2">
                Send Message <Send className="h-5 w-5" />
              </span>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
